import { ButtonGroup, LinkButton } from "lib/miolo";
import { FORM_ERROR } from "final-form";
import { useField, useForm } from "react-final-form-hooks";
import restartForm from "lib/utils/form/restartForm";
import classNames from "components/ui/classNames";
import { Button } from "components/ui/Button";
import checkCanSubmit from "../../../lib/utils/form/checkCanSubmit";
import validateFormValuesAgainstSchema from "../../../lib/utils/form/validateFormValuesAgainstSchema";
import InlineErrorSpan from "../../shared/form/InlineErrorSpan";
import CommentInputTextArea from "../CommentInputTextArea";
import commentInputFormDataShape from "./shapes/commentInputFormDataShape";
import translateServerErrorIntoFriendlyMessage from "./translateServerErrorIntoFriendlyMessage";

interface IIndividualCommentInputFormProps {
  bodyAsPlainText?: string;
  submitButtonText: string;
  autoFocus?: boolean;
  onCancel: () => void;
  onSubmit: (bodyAsPlainText: string) => Promise<any>;
}

export default function IndividualCommentInputForm({
  bodyAsPlainText = "",
  submitButtonText,
  autoFocus,
  onCancel,
  onSubmit,
}: IIndividualCommentInputFormProps) {
  const { form, handleSubmit, submitting, submitError, ...props } = useForm<{
    bodyAsPlainText: string;
  }>({
    onSubmit: async ({ bodyAsPlainText }) => {
      try {
        await onSubmit(bodyAsPlainText);
      } catch (err) {
        if (err instanceof Error) {
          return {
            [FORM_ERROR]: translateServerErrorIntoFriendlyMessage(err),
          };
        }
        return {
          [FORM_ERROR]: "Erro desconhecido",
        };
      }

      return null;
    },
    validate: validateFormValuesAgainstSchema(commentInputFormDataShape),
    initialValues: {
      bodyAsPlainText,
    },
  });

  const bodyAsPlainTextField = useField("bodyAsPlainText", form);
  const canSubmit = submitting ? false : checkCanSubmit(props);

  return (
    <form onSubmit={handleSubmit} className="grid gap-y-5">
      <CommentInputTextArea
        {...bodyAsPlainTextField.input}
        autoFocus={autoFocus}
        onKeyDown={(event) => {
          if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
            handleSubmit();
          }
        }}
        placeholder="Escreva alguma coisa aqui..."
      />

      <div className="grid grid-cols-2 items-center">
        <div>
          {bodyAsPlainTextField.meta.touched && bodyAsPlainTextField.meta.error && (
            <InlineErrorSpan>{bodyAsPlainTextField.meta.error}</InlineErrorSpan>
          )}
        </div>

        <div className="flex justify-end font-normal">
          <ButtonGroup stack="horizontally" spacing={20}>
            <LinkButton
              disabled={submitting}
              variant="black"
              onClick={() => {
                restartForm(form);
                onCancel();
              }}
            >
              Cancelar
            </LinkButton>

            <Button
              className={classNames(
                "flex h-9 w-auto select-none items-center justify-center overflow-visible rounded border-none",
                "bg-brand px-3 text-sm text-white shadow-md hover:bg-brand/50 focus:outline-none",
                {
                  "cursor-default bg-brand/50": !canSubmit,
                },
              )}
              type="submit"
              disabled={!canSubmit}
            >
              {submitting ? "Aguarde..." : submitButtonText}
            </Button>
          </ButtonGroup>
        </div>
      </div>

      {submitError && (
        <div className="rounded-md bg-red-100 p-3 text-red-700">
          <p className="m-0 text-sm tracking-wider">{submitError}</p>
        </div>
      )}
    </form>
  );
}
